import ExerciseLibrary from "@/Components/Exercises/ExerciseLibrary";
import { Exercise } from "@/Components/Exercises/exerciseSchema";
import DataLoadingModal from "@/Components/UI/DataLoadingModal";
import { ExercisePickerContext } from "@/Contexts/ExercisePickerContext";
import { ClientExercise } from "@/types/exercise";
import { Client, Coach, PageProps } from "@/types/page";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import { PropsWithChildren, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa6";

type ExercisePickerPageProps = PageProps<{ otherUser?: Client | Coach }>;

export function ExercisePickerProvider({ children }: PropsWithChildren) {
    const { user, otherUser } = usePage<ExercisePickerPageProps>().props;

    const [selectedExercises, setSelectedExercises] = useState<
        Record<number, Exercise | null>
    >({});
    const [isSelecting, setIsSelecting] = useState(false);
    const [exercises, setExercises] = useState<{
        exercises: Exercise[];
        latestUniqueClientExercises: ClientExercise[];
    }>();

    const showExercisePicker = () => {
        setIsSelecting(true);
    };

    const setSelectedExercise = (
        otherUserId: number,
        exercise: Exercise | null,
    ) => {
        setSelectedExercises((previousSelectedExercises) => ({
            ...previousSelectedExercises,
            [otherUserId]: exercise,
        }));
    };

    const clearSelectedExercise = (otherUserId: number) => {
        setSelectedExercise(otherUserId, null);
    };

    const attachExercise = async (
        otherUserId: number,
        exerciseId: Exercise["id"],
    ) => {
        const exercise = (
            await axios.get<Exercise>(
                route("exercises.show", { exercise: exerciseId }),
            )
        ).data;
        setSelectedExercise(otherUserId, exercise);
    };

    const makeExerciseForClient = async (
        exercise: Exercise,
        clientId: number,
    ) => {
        const response = await axios.post<ClientExercise>(
            route("client-exercises.store"),
            {
                exercise_id: exercise.id,
                client_id: clientId,
            },
        );
        return response.data;
    };

    useEffect(() => {
        setSelectedExercises({});
        setIsSelecting(false);
        setExercises(undefined);
    }, [user?.id, user?.type]);

    useEffect(() => {
        setIsSelecting(false);
        setExercises(undefined);
    }, [otherUser?.id]);

    return (
        <ExercisePickerContext.Provider
            value={{
                selectedExercises,
                attachExercise,
                showExercisePicker,
                clearSelectedExercise,
                makeExerciseForClient,
            }}
        >
            {children}
            {user?.type === "coach" && otherUser && (
                <DataLoadingModal<{
                    exercises: Exercise[];
                    latestUniqueClientExercises: ClientExercise[];
                }>
                    id="exercise-picker-modal"
                    testId="exercise-assignment-modal"
                    show={isSelecting}
                    onClose={() => {
                        setIsSelecting(false);
                        setExercises(undefined);
                    }}
                    route={route("exercises.index", { clientId: otherUser.id })}
                    modalData={exercises}
                    finishedLoadingCallback={setExercises}
                    animateClose={false}
                    showCloseButton={true}
                    maxWidth="9xl"
                >
                    <div className="p-8">
                        <div
                            data-testid="exercise-picker-modal-header"
                            className="mb-4 text-xl font-bold"
                        >
                            Attach an Exercise
                        </div>
                        <ExerciseLibrary
                            exercises={exercises?.exercises ?? []}
                            latestUniqueClientExercises={
                                exercises?.latestUniqueClientExercises
                            }
                            buttons={[
                                {
                                    id: "attach",
                                    tooltipTitle: "Attach to Message",
                                    buttonIcon: <FaPlus size={16} />,
                                    onClick: (exercise) => {
                                        setSelectedExercise(
                                            otherUser.id,
                                            exercise,
                                        );
                                        setIsSelecting(false);
                                        setExercises(undefined);
                                    },
                                },
                            ]}
                            clientId={otherUser.id}
                        />

                        <div className="mt-4">
                            If you know of other exercises that could be
                            beneficial, please{" "}
                            <button
                                data-testid="exercise-feedback-button"
                                className="text-brand-primary text-brand-red underline"
                                onClick={() => {
                                    window.open(
                                        "https://forms.gle/t89J4TUfBfTptASk7",
                                        "_blank",
                                    );
                                }}
                            >
                                Submit Feedback
                            </button>
                        </div>
                    </div>
                </DataLoadingModal>
            )}
        </ExercisePickerContext.Provider>
    );
}
